/**
 * Grid profile (Wave 3.5): sample several grids along a drawn polyline and find the
 * soundings near it. Pure (runs in the geo worker via the `gridProfile` task); the same
 * result shape as `POST /api/analysis/grid-profile`, which is the fallback when the
 * worker is unavailable or the client does not hold the grid.
 */
import { profileAlongLineStep } from './grid-ops';

export const MAX_PROFILE_SAMPLES = 5000;

function metricFrame(coords) {
  const midLat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  return { mLat: 110540, mLon: 111320 * Math.cos((midLat * Math.PI) / 180) };
}

/** Points within `bufferM` of the polyline, with chainage `dist` and perpendicular `offset` (m). */
export function soundingsNearLine(points, coords, bufferM = 50) {
  if (!coords || coords.length < 2) return [];
  const { mLat, mLon } = metricFrame(coords);
  const xy = coords.map((c) => [c[0] * mLon, c[1] * mLat]);
  const cum = [0];
  for (let i = 1; i < xy.length; i++) cum.push(cum[i - 1] + Math.hypot(xy[i][0] - xy[i - 1][0], xy[i][1] - xy[i - 1][1]));
  const out = [];
  for (const p of points) {
    if (!Number.isFinite(p.lon) || !Number.isFinite(p.lat)) continue;
    const px = p.lon * mLon, py = p.lat * mLat;
    let best = null;
    for (let i = 0; i < xy.length - 1; i++) {
      const [ax, ay] = xy[i];
      const dx = xy[i + 1][0] - ax, dy = xy[i + 1][1] - ay;
      const len2 = dx * dx + dy * dy;
      const t = len2 > 0 ? Math.min(1, Math.max(0, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0;
      const d = Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
      if (!best || d < best.d) best = { d, dist: cum[i] + t * Math.sqrt(len2) };
    }
    if (best && best.d <= bufferM) out.push({ ...p, dist: Math.round(best.dist * 100) / 100, offset: Math.round(best.d * 100) / 100 });
  }
  return out.sort((a, b) => a.dist - b.dist);
}

/**
 * @param {{ grids: {name, grid, bbox}[], line: number[][], step: number, points?: object[], buffer?: number }} req
 * @returns {{ length_m, step_m, series: string[], samples: {dist, lon, lat, values}[], soundings: object[] }}
 */
export function computeGridProfile({ grids, line, step = 10, points = [], buffer = 50 }) {
  const perGrid = grids.map((g, i) => ({
    name: g.name || `Grid ${i + 1}`,
    rows: profileAlongLineStep({ grid: g.grid, bbox: g.bbox }, line, step),
  }));
  const base = perGrid[0]?.rows || [];
  if (base.length > MAX_PROFILE_SAMPLES) {
    throw new Error(`Step too small: ${base.length} samples exceeds the ${MAX_PROFILE_SAMPLES} limit`);
  }
  const samples = base.map((s, k) => ({
    dist: s.dist,
    lon: Math.round(s.lon * 1e6) / 1e6,
    lat: Math.round(s.lat * 1e6) / 1e6,
    values: Object.fromEntries(perGrid.map((pg) => {
      const v = pg.rows[k].value;
      return [pg.name, Number.isNaN(v) ? null : Math.round(v * 1e4) / 1e4];
    })),
  }));
  return {
    length_m: base.length ? base[base.length - 1].dist : 0,
    step_m: step,
    series: perGrid.map((pg) => pg.name),
    samples,
    soundings: points.length ? soundingsNearLine(points, line, buffer) : [],
  };
}

/** Point features of the given layers as `{ lon, lat, label, layer, value }`. */
export function collectSoundings(layers) {
  const out = [];
  for (const l of layers) {
    for (const f of l.geojson?.features || []) {
      if (f.geometry?.type !== 'Point') continue;
      const [lon, lat] = f.geometry.coordinates;
      const p = f.properties || {};
      out.push({ lon, lat, layer: l.name, label: p.name ?? p.label ?? p.id ?? p.station ?? '', value: p.value ?? null });
    }
  }
  return out;
}

const csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** CSV of the samples: distance, lon, lat, one column per grid (empty = no data). */
export function profileToCsv(profile) {
  const head = ['distance_m', 'lon', 'lat', ...profile.series];
  const rows = profile.samples.map((s) => [s.dist, s.lon, s.lat, ...profile.series.map((n) => s.values[n])]);
  return [head, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
}
